import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { addContact, ContactLinkOtherClusterError, createOrGetMyContactLink, resolveContactLink, resetMyContactLink, type ContactLinkDto, type ContactLinkPreviewDto } from '../api/contacts';
import { ApiError } from '../api/client';
import { contactPageUrl } from '../lib/contactQr';
import { backdropClose } from '../lib/modalBackdrop';
import { intlLocale } from '../lib/language';
import ConfirmModal from './ConfirmModal';
import Avatar from './Avatar';
import type { UserDto } from '../api/types';

interface MyQrProps {
  me: UserDto;
  onClose: () => void;
}

export function MyContactQrModal({ me, onClose }: MyQrProps) {
  const { t, i18n } = useTranslation('qr');
  const locale = intlLocale(i18n.resolvedLanguage);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [link, setLink] = useState<ContactLinkDto | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    createOrGetMyContactLink()
      .then((value) => { if (alive) setLink(value); })
      .catch(() => { if (alive) setError(t('loadProfileLinkFailed')); });
    return () => { alive = false; };
  }, [t]);

  const linkExpiryMs = link ? Date.parse(link.expiresAt) : Number.NaN;
  const linkExpired = !!link && (!Number.isFinite(linkExpiryMs) || linkExpiryMs <= Date.now());

  useEffect(() => {
    const expiresAt = link ? Date.parse(link.expiresAt) : Number.NaN;
    if (!link || !canvas.current || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) return;
    QRCode.toCanvas(canvas.current, contactPageUrl(link), {
      width: 220,
      margin: 4,
      color: { dark: '#000000', light: '#ffffff' },
    }).catch(() => setError(t('drawQrFailed')));
  }, [link, t]);

  useEffect(() => {
    if (!link) return;
    let alive = true;
    const remaining = Date.parse(link.expiresAt) - Date.now();
    const renewExpiredLink = () => {
      setError(t('expiredRefreshing'));
      createOrGetMyContactLink()
        .then((value) => {
          if (!alive) return;
          if (Date.parse(value.expiresAt) > Date.now()) {
            setError('');
            setLink(value);
          } else {
            setError(t('staleLink'));
          }
        })
        .catch(() => {
          if (alive) setError(t('refreshFailed'));
        });
    };
    if (!Number.isFinite(remaining) || remaining <= 0) {
      renewExpiredLink();
      return () => { alive = false; };
    }
    const timeout = window.setTimeout(renewExpiredLink, remaining);
    return () => {
      alive = false;
      window.clearTimeout(timeout);
    };
  }, [link, t]);

  const copy = async () => {
    if (!link || linkExpired) return;
    try {
      await navigator.clipboard.writeText(contactPageUrl(link));
      setCopied(true);
    } catch {
      setError(t('copyFailed'));
    }
  };

  const reset = async () => {
    setConfirmRevoke(false);
    setBusy(true);
    setError('');
    try {
      const replacement = await resetMyContactLink();
      setLink(replacement);
      setCopied(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('resetFailed'));
    } finally {
      setBusy(false);
      setConfirmRevoke(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" {...backdropClose(() => { if (!busy) onClose(); })}>
        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380, textAlign: 'center' }}>
          <button type="button" className="modal-close" title={t('close')} onClick={onClose} disabled={busy}>✕</button>
          <h2>{t('myTitle')}</h2>
          <div style={{ color: 'var(--muted)', marginBottom: 12 }}>{me.displayName} · @{me.username}</div>
          <div className="qr-login-box" style={{ margin: '0 auto 12px' }}>
            {link && !linkExpired ? <canvas ref={canvas} /> : !error && <span className="spinner" />}
          </div>
          {error && <div className="error" style={{ marginBottom: 10 }}>{error}</div>}
          {error && (!link || linkExpired) && (
            <button type="button" className="btn btn-ghost" onClick={() => {
              setError('');
              createOrGetMyContactLink()
                .then((value) => {
                  if (Date.parse(value.expiresAt) > Date.now()) setLink(value);
                  else setError(t('staleLink'));
                })
                .catch(() => setError(t('loadProfileLinkFailed')));
            }}>{t('retry')}</button>
          )}
          {link && !linkExpired && (
            <>
              <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 12 }}>
                {t('expiresAt', { date: new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }).format(new Date(link.expiresAt)) })}
              </div>
              <div className="row" style={{ justifyContent: 'center', gap: 8 }}>
                <button type="button" className="btn" onClick={copy} disabled={busy}>{copied ? t('copied') : t('copyLink')}</button>
                <div style={{ position: 'relative' }}>
                  <button type="button" className="btn btn-ghost" title={t('misc:contactQrMoreActions')} onClick={() => setActionsOpen((v) => !v)} disabled={busy}>⋮</button>
                  {actionsOpen && (
                    <div className="menu" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 5 }}>
                      <button type="button" className="menu-item danger" onClick={() => { setActionsOpen(false); setConfirmRevoke(true); }}>
                        {t('misc:contactQrResetLink')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {confirmRevoke && (
        <ConfirmModal
          message={t('misc:contactQrResetConfirm')}
          confirmLabel={t('misc:contactQrReset')}
          onCancel={() => setConfirmRevoke(false)}
          onConfirm={reset}
        />
      )}
    </>
  );
}

interface PreviewProps {
  clusterId: string;
  token: string;
  onClose: () => void;
  onAdded: () => void;
  onOpenChat: (userId: string) => Promise<void> | void;
}

export function ContactLinkPreviewModal({ clusterId, token, onClose, onAdded, onOpenChat }: PreviewProps) {
  const { t } = useTranslation('qr');
  const [preview, setPreview] = useState<ContactLinkPreviewDto | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    resolveContactLink(clusterId, token)
      .then((value) => { if (alive) setPreview(value); })
      .catch((e: unknown) => {
        if (!alive) return;
        setError(
          e instanceof ContactLinkOtherClusterError || e instanceof ApiError
            ? e.message
            : t('misc:contactQrCheckFailed'),
        );
      });
    return () => { alive = false; };
  }, [clusterId, token, t]);

  const add = async () => {
    if (!preview || busy || preview.isSelf || preview.isContact) return;
    setBusy(true);
    setError('');
    try {
      await addContact(preview.userId);
      setPreview({ ...preview, isContact: true });
      onAdded();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('misc:contactQrAddFailed'));
    } finally {
      setBusy(false);
    }
  };

  const openChat = async () => {
    if (!preview || busy) return;
    setBusy(true);
    setError('');
    try {
      await onOpenChat(preview.userId);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('misc:contactQrOpenChatFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" {...backdropClose(() => { if (!busy) onClose(); })}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
        <button type="button" className="modal-close" title={t('close')} onClick={onClose} disabled={busy}>✕</button>
        <h2>{t('misc:contactQrProfileTitle')}</h2>
        {error && <div className="error" style={{ marginBottom: 10 }}>{error}</div>}
        {!preview && !error && <div className="spinner" />}
        {preview && (
          <div style={{ textAlign: 'center', margin: '12px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
              <Avatar userId={preview.userId} name={preview.displayName || preview.username} version={preview.avatarFileId} size={54} />
            </div>
            <div style={{ fontWeight: 600 }}>{preview.displayName}</div>
            <div style={{ color: 'var(--muted)', fontSize: 13 }}>@{preview.username}</div>
            <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 10 }}>
              {preview.isSelf ? t('misc:contactQrSelf') : preview.isContact ? t('misc:contactQrAlreadyAdded') : t('misc:contactQrAddQuestion')}
            </div>
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>{t('misc:contactQrClose')}</button>
          {preview && !preview.isSelf && !preview.isContact && (
            <button type="button" className="btn" onClick={add} disabled={busy}>{busy ? t('misc:contactQrAdding') : t('misc:contactQrAdd')}</button>
          )}
          {preview && !preview.isSelf && preview.isContact && (
            <button type="button" className="btn" onClick={openChat} disabled={busy}>{busy ? t('misc:contactQrOpening') : t('misc:contactQrOpenChat')}</button>
          )}
        </div>
      </div>
    </div>
  );
}
